import { createStore } from './createStore';

const DEFAULT_IMAGE_MODEL = 'gpt-image-2';
// Old sentinel values that were previously used as system defaults — treat as unset.
const LEGACY_DEFAULT_MODELS = new Set(['auto', 'nano-banana', 'nano-banana-pro', 'nano-banana-2']);

// ── Video model / duration persistence ──────────────────────────────────
// Keys are intentionally generic; model capabilities now drive the video UI.
const VIDEO_MODEL_UNSET = new Set(['auto', 'pending', '']);

function loadPersistedVideoModel() {
  try {
    const raw = localStorage.getItem('bt:videoModel');
    return raw && raw.trim() && !VIDEO_MODEL_UNSET.has(raw.trim().toLowerCase()) ? raw.trim() : null;
  } catch (e) {
    return null;
  }
}

function loadPersistedVideoDuration() {
  try {
    const raw = localStorage.getItem('bt:videoDuration');
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? String(n) : null;
  } catch (e) {
    return null;
  }
}

function persistVideoModel(model) {
  try {
    localStorage.setItem('bt:videoModel', model ?? '');
  } catch (e) { /* ignore */ }
}

function persistVideoDuration(duration) {
  try {
    localStorage.setItem('bt:videoDuration', duration ?? '');
  } catch (e) { /* ignore */ }
}

function loadPersistedImageGenerationModel() {
  try {
    // Back-compat: fall back to the old shared key if present.
    const raw = localStorage.getItem('bt:imageGenerationModel') || localStorage.getItem('bt:imageModel');
    return raw && raw.trim() && !LEGACY_DEFAULT_MODELS.has(raw.trim()) ? raw.trim() : DEFAULT_IMAGE_MODEL;
  } catch (e) {
    return DEFAULT_IMAGE_MODEL;
  }
}

function loadPersistedImageEditModel() {
  try {
    const raw = localStorage.getItem('bt:imageEditModel');
    return raw && raw.trim() && !LEGACY_DEFAULT_MODELS.has(raw.trim()) ? raw.trim() : DEFAULT_IMAGE_MODEL;
  } catch (e) {
    return DEFAULT_IMAGE_MODEL;
  }
}

const initialState = {
  sidebarMode: 'default',
  explicitCanvasMode: null,
  selectedKeyframe: null,
  showStoryboardOverVideo: false,
  previewingKeyframeVersionNumber: null, // Version number being previewed in keyframe editor
  isPlaying: false,
  showReplayOverlay: false,
  currentPlaybackTime: 0,
  timelineZoom: 1,
  thumbnails: {},
  videoDurations: {},
  audioWaveforms: {},
  // Audio source durations (ms) keyed by audioClipId
  audioSourceDurations: {},
  // Video cache: { [id]: { duration, blobUrl, thumbnails, status, sourceUrl } }
  // `sourceUrl` is the network URL this blob/thumbnails were derived from, so we can avoid stale playback
  // after regenerations (when the URL changes but an old blob is still in memory).
  // status: 'pending' | 'loading' | 'loaded' | 'error'
  videoCache: {},

  // Image model selection (separate for create vs edit flows)
  imageGenerationModel: loadPersistedImageGenerationModel(),
  imageEditModel: loadPersistedImageEditModel(),
  // Back-compat field (some callsites might still read imageModel)
  imageModel: loadPersistedImageGenerationModel(),

  videoModel: loadPersistedVideoModel(),
  videoDuration: loadPersistedVideoDuration(),
};

/**
 * Pure UI state for the editor shell (no server data).
 */
export const useEditorUIStore = createStore('EditorUIStore', (set) => ({
  ...initialState,

  setSidebarMode: (sidebarMode) => set({ sidebarMode }),
  setExplicitCanvasMode: (explicitCanvasMode) => set({ explicitCanvasMode }),
  setPreviewingKeyframeVersionNumber: (vn) => set({ previewingKeyframeVersionNumber: vn ?? null }),

  setImageGenerationModel: (imageGenerationModel) => {
    const next = (imageGenerationModel ?? '').toString();
    try {
      localStorage.setItem('bt:imageGenerationModel', next);
      // Keep legacy key in sync for older builds/tools
      localStorage.setItem('bt:imageModel', next);
    } catch (e) {
      // Ignore persistence errors (private mode, etc.)
    }
    set({ imageGenerationModel: next, imageModel: next });
  },

  setImageEditModel: (imageEditModel) => {
    const next = (imageEditModel ?? '').toString();
    try {
      localStorage.setItem('bt:imageEditModel', next);
    } catch (e) {
      // Ignore persistence errors (private mode, etc.)
    }
    set({ imageEditModel: next });
  },

  // Back-compat alias: treat setImageModel as setting the generation model.
  setImageModel: (imageModel) => {
    const next = (imageModel ?? '').toString();
    try {
      localStorage.setItem('bt:imageGenerationModel', next);
      localStorage.setItem('bt:imageModel', next);
    } catch (e) {
      // Ignore persistence errors (private mode, etc.)
    }
    set({ imageGenerationModel: next, imageModel: next });
  },

  setVideoModel: (model) => {
    const next = (model ?? '').toString();
    persistVideoModel(next);
    set((state) => {
      const normalized = next || null;
      if (state.videoModel === normalized) return state;
      return { videoModel: normalized };
    });
  },

  setVideoDuration: (duration) => {
    const next = (duration ?? '').toString();
    persistVideoDuration(next);
    set((state) => {
      const normalized = next || null;
      if (state.videoDuration === normalized) return state;
      return { videoDuration: normalized };
    });
  },

  setSelectedKeyframe: (selectedKeyframe) =>
    set({ selectedKeyframe }),

  toggleStoryboardOverVideo: () =>
    set((state) => ({
      showStoryboardOverVideo: !state.showStoryboardOverVideo,
    })),
  setShowStoryboardOverVideo: (value) =>
    set({ showStoryboardOverVideo: Boolean(value) }),

  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setShowReplayOverlay: (showReplayOverlay) => set({ showReplayOverlay }),
  setCurrentPlaybackTime: (currentPlaybackTime) => set({ currentPlaybackTime }),

  setTimelineZoom: (timelineZoom) => set({ timelineZoom }),
  setThumbnails: (thumbnails) => set({ thumbnails }),
  setVideoDurations: (videoDurations) => set({ videoDurations }),
  setAudioWaveforms: (audioWaveforms) => set({ audioWaveforms }),
  setAudioSourceDurations: (audioSourceDurations) => set({ audioSourceDurations }),
  updateAudioSourceDuration: (audioClipId, sourceDurationMs) =>
    set((state) => ({
      audioSourceDurations: {
        ...state.audioSourceDurations,
        [audioClipId]: Math.max(0, Math.round(Number(sourceDurationMs) || 0)),
      },
    })),

  // Video cache actions
  setVideoCache: (videoCache) => set({ videoCache }),
  
  updateVideoCache: (sceneId, updates) =>
    set((state) => ({
      videoCache: {
        ...state.videoCache,
        [sceneId]: {
          ...(state.videoCache[sceneId] || {}),
          ...updates,
        },
      },
      // Also sync duration to videoDurations for backward compatibility
      ...(updates.duration !== undefined
        ? {
            videoDurations: {
              ...state.videoDurations,
              [sceneId]: updates.duration,
            },
          }
        : {}),
    })),

  clearVideoCache: (sceneId) =>
    set((state) => {
      if (sceneId) {
        // Clear specific scene's cache and revoke blob URL
        const entry = state.videoCache[sceneId];
        if (entry?.blobUrl) {
          URL.revokeObjectURL(entry.blobUrl);
        }
        const { [sceneId]: removed, ...rest } = state.videoCache;
        return { videoCache: rest };
      }
      // Clear all - revoke all blob URLs
      Object.values(state.videoCache).forEach((entry) => {
        if (entry?.blobUrl) {
          URL.revokeObjectURL(entry.blobUrl);
        }
      });
      return { videoCache: {} };
    }),

  resetEditorUI: () =>
    set((state) => {
      // Revoke all blob URLs before reset
      Object.values(state.videoCache).forEach((entry) => {
        if (entry?.blobUrl) {
          URL.revokeObjectURL(entry.blobUrl);
        }
      });
      return initialState;
    }),
}));


